/**
 * Shipping API service (S6.1) — 出貨通知 Shipping Notice + 裝櫃批次 Shipment
 * Batch, split out of the codex multi-page seam onto the house thin-service
 * contract (goods-receipt calls moved to W5's procurement surface).
 *
 * Server-enforced gates this service fronts (mocks/handlers/shipping.ts):
 * - cut-off requires 櫃號 containerNumber + sealNumber (400)
 * - qtyLoaded ≤ qtyPlanned per line (400)
 * - short-load → owned 訂單異常 (owner 船務, due window) on the OCC line
 * - depart requires status cut_off (409); loaded qty is FIXED at seal
 * - notice send/acknowledge feed the A2 出貨通知再確認 quantity buckets
 */

import apiClient from './axios.config';
import { API_ROUTES } from '@/constants/api.constants';
import type { ShipmentBatch, ShippingNotice } from '@/types/api';

export interface CreateShipmentBatchRequest {
  vesselOrCarrier: string;
  etd: string;
  eta: string;
  poIds: string[];
}

export interface CutOffShipmentBatchRequest {
  containerNumber: string;
  sealNumber: string;
  lines: Array<{ id: string; qtyLoaded: number; cbm?: number; grossWeightKg?: number }>;
}

export interface CreateShippingNoticeRequest {
  batchId: string;
  vendorId: number;
  vendorName: string;
  lines: Array<{ poLineId: string; qtyEstimated: number }>;
}

export type CounterSignShippingNoticeLines = Array<{ poLineId: string; qtyConfirmed: number }>;

export const shippingApi = {
  // -- 裝櫃批次 Shipment batches ---------------------------------------------

  listBatches: async (): Promise<{ data: ShipmentBatch[] }> => {
    const { data } = await apiClient.get<{ data: ShipmentBatch[] }>(API_ROUTES.SHIPMENT_BATCHES);
    return data;
  },

  createBatch: async (body: CreateShipmentBatchRequest): Promise<{ data: ShipmentBatch }> => {
    const { data } = await apiClient.post<{ data: ShipmentBatch }>(
      API_ROUTES.SHIPMENT_BATCHES,
      body
    );
    return data;
  },

  /** 封櫃截單 — container + seal required; qtyLoaded fixed here, never after. */
  cutOffBatch: async (
    id: string,
    body: CutOffShipmentBatchRequest
  ): Promise<{ data: ShipmentBatch }> => {
    const { data } = await apiClient.post<{ data: ShipmentBatch }>(
      `${API_ROUTES.SHIPMENT_BATCHES}/${id}/cut-off`,
      body
    );
    return data;
  },

  /** 離港 — server 409s unless the batch is already cut off. */
  departBatch: async (id: string): Promise<{ data: ShipmentBatch }> => {
    const { data } = await apiClient.post<{ data: ShipmentBatch }>(
      `${API_ROUTES.SHIPMENT_BATCHES}/${id}/depart`
    );
    return data;
  },

  // -- 出貨通知 Shipping notices (白單 issue / 黃單 counter-sign) -------------

  listNotices: async (): Promise<{ data: ShippingNotice[] }> => {
    const { data } = await apiClient.get<{ data: ShippingNotice[] }>(API_ROUTES.SHIPPING_NOTICES);
    return data;
  },

  createNotice: async (body: CreateShippingNoticeRequest): Promise<{ data: ShippingNotice }> => {
    const { data } = await apiClient.post<{ data: ShippingNotice }>(
      API_ROUTES.SHIPPING_NOTICES,
      body
    );
    return data;
  },

  /** 發出白單 — issue the notice to the factory (feeds estimated buckets). */
  sendNotice: async (id: string): Promise<{ data: ShippingNotice }> => {
    const { data } = await apiClient.post<{ data: ShippingNotice }>(
      `${API_ROUTES.SHIPPING_NOTICES}/${id}/send`
    );
    return data;
  },

  /** 黃單回簽 — factory counter-sign capture; feeds A2 出貨通知再確認. */
  acknowledgeNotice: async (
    id: string,
    lines: CounterSignShippingNoticeLines
  ): Promise<{ data: ShippingNotice }> => {
    const { data } = await apiClient.post<{ data: ShippingNotice }>(
      `${API_ROUTES.SHIPPING_NOTICES}/${id}/acknowledge`,
      { lines }
    );
    return data;
  },
};

export default shippingApi;
